/**
 * Interviewer Dashboard Component
 * Coláiste Lurgan Management System - Interview-focused dashboard for interviewers
 */

import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { LoadingSpinner } from '../ui/loading';
import {
  Users,
  Calendar,
  ClipboardList,
  Star,
} from 'lucide-react';
import { useInterviewerAnalytics } from '../../hooks/useDashboardAnalytics';

export function InterviewerDashboard() {
  const { t } = useTranslation();
  const { data: analytics, isLoading, error } = useInterviewerAnalytics();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-destructive">{t('dashboard.error', 'Error loading dashboard data')}</p>
          <p className="text-sm text-muted-foreground mt-2">{error.message}</p>
        </div>
      </div>
    );
  }

  const { kpis, courses } = analytics || {};

  // Format date in shorter format (no year)
  const formatShortDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-GB', { month: 'short' });
    return `${day} ${month}`;
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Header with KPIs */}
      <div className="flex items-start justify-between gap-8">
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">
            {t('interviewDashboard.title', 'Interview Overview')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('interviewDashboard.subtitle', 'Your interview progress across courses')}
          </p>
        </div>

        {/* KPI Stats in Header */}
        <div className="flex gap-8">
          {/* Total Pending */}
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-amber-100">
              <ClipboardList className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {t('interviewDashboard.totalPending', 'Total Pending')}
              </p>
              <p className="text-2xl font-bold">{kpis?.total_pending || 0}</p>
            </div>
          </div>

          {/* Courses with Pending */}
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-blue-100">
              <Calendar className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {t('interviewDashboard.pendingCourses', 'Pending Courses')}
              </p>
              <p className="text-2xl font-bold">{kpis?.courses_with_pending || 0}</p>
            </div>
          </div>

          {/* My Average Score */}
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-primary/10">
              <Star className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {t('interviewDashboard.myAvgScore', 'My Avg Score')}
              </p>
              <p className="text-2xl font-bold">
                {kpis?.my_avg_score ? kpis.my_avg_score.toFixed(1) : '—'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Course Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {courses?.map((course) => (
          <Card key={course.uuid} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg">{course.name}</CardTitle>
                  <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>{formatShortDate(course.start_date)} - {formatShortDate(course.end_date)}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-primary">{course.student_count}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {t('dashboard.students', 'Students')}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Interview Stats */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <ClipboardList className="h-4 w-4" />
                  {t('interviewDashboard.interviews', 'Interviews')}
                </div>
                <div className="pl-6 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t('interviewDashboard.pending', 'Pending')}:
                    </span>
                    <span className={`font-medium ${course.pending_count > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                      {course.pending_count}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t('interviewDashboard.scored', 'Scored')}:
                    </span>
                    <span className="font-medium">{course.scored_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t('interviewDashboard.notRequired', 'Not Required')}:
                    </span>
                    <span className="font-medium text-muted-foreground">{course.not_required_count}</span>
                  </div>
                </div>
              </div>

              {/* My Scores */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Star className="h-4 w-4 text-primary" />
                  {t('interviewDashboard.myScores', 'My Scores')}
                </div>
                <div className="pl-6 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t('interviewDashboard.completed', 'Completed')}:
                    </span>
                    <span className="font-medium">{course.my_completed}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t('interviewDashboard.avgScore', 'Avg Score')}:
                    </span>
                    <span className="font-medium">
                      {course.my_avg_score ? course.my_avg_score.toFixed(1) : '—'}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty state */}
      {(!courses || courses.length === 0) && (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium">
            {t('interviewDashboard.noCourses', 'No upcoming courses')}
          </h3>
          <p className="text-muted-foreground mt-1">
            {t('interviewDashboard.noCoursesDescription', 'There are no active courses with students to interview.')}
          </p>
        </div>
      )}
    </div>
  );
}

export default InterviewerDashboard;
